// Login (spec R10.3, admin mockup v2 §4): e-mail first, then whatever that user's method is —
// password and a one-time code, or Microsoft Entra ID. Shift login on an enrolled device is
// the other screen here: the device is the first factor, a short code or badge the second.

import { useEffect, useRef, useState } from "react";
import { Auth, adoptSession, applyPrefs, getDevice, getPrefs, type DeviceInfo, type LoginStep, type TenantChoice } from "./api/client.js";
import { t } from "./i18n.js";
import "./admin/admin.css";
import { attachWedge } from "./scan/wedge.js";
import { feedback, unlockAudio } from "./scan/feedback.js";
import { Actions, Banner, Brand, Btn, Prompt, ScanBar, Strip, focusScan } from "./ui/components.jsx";

/** A pending tenant choice handed back by the Entra callback (fragment `choose` + `tenants`). */
export interface PendingChoice {
  choice: string;
  tenants: TenantChoice[];
}

export function Login({ onDone, onDevSettings, notice, pending }: { onDone: () => void; onDevSettings: () => void; notice?: string | undefined; pending?: PendingChoice | undefined }) {
  const [email, setEmail] = useState("");
  const [step, setStep] = useState<"email" | "password" | "tenant" | "mfa">(pending ? "tenant" : "email");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [challenge, setChallenge] = useState("");
  // One person, several companies (owner 2026-10-29): when the address is a user in more than
  // one tenant the server answers with a choice instead of a session, and this step shows it.
  const [choice, setChoice] = useState<PendingChoice | null>(pending ?? null);
  // The choice from the Entra callback arrives after this screen is already on (the app reads
  // the fragment in an effect), so a later `pending` moves the screen to the choice too.
  useEffect(() => {
    if (pending) {
      setChoice(pending);
      setStep("tenant");
    }
  }, [pending]);
  // One e-mail field (owner 2026-10-28): the server knows each user's method and the next
  // step is Microsoft, with the address filled in, or the password field.
  const [error, setError] = useState<string | null>(notice ?? null);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await Auth.start(email);
      if (r.method === "password") setStep("password");
      else if (r.method === "entra") window.location.href = Auth.entraStartUrl(email);
      else setError(t("login.unknown"));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const submitPassword = async () => {
    setBusy(true);
    setError(null);
    try {
      await follow(await Auth.password(email, password));
    } catch {
      setError(t("login.failed"));
    } finally {
      setBusy(false);
    }
  };
  /** The next step after a first factor or a tenant choice: the choice, the code, or in. */
  const follow = async (r: LoginStep) => {
    if (r.chooseTenant) {
      setChoice({ choice: r.choice, tenants: r.tenants });
      setStep("tenant");
    } else if (r.mfaRequired) {
      setChallenge(r.challenge);
      setStep("mfa");
    } else {
      sessionStorage.removeItem("trail.stayTask");
      await adoptSession(r.token, r.expiresAt);
      onDone();
    }
  };
  const pick = async (tenantId: string) => {
    if (!choice) return;
    setBusy(true);
    setError(null);
    try {
      await follow(await Auth.choose(choice.choice, tenantId));
    } catch {
      setError(t("login.failed"));
      setStep("email");
    } finally {
      setBusy(false);
    }
  };
  const roleNames = (roles: string[]) => roles.map((r) => t(`adm.role.${r}` as Parameters<typeof t>[0])).join(", ");
  const submitCode = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await Auth.mfa(challenge, code);
      await adoptSession(r.token, r.expiresAt);
      onDone();
    } catch {
      setError(t("login.badCode"));
    } finally {
      setBusy(false);
    }
  };

  // On a computer the page is the approved desktop mockup (admin-v1/profiles.html §4): a
  // centred card in the office theme, Microsoft first when it exists. Phones keep the task
  // client's large controls.
  const desktop = window.innerWidth >= 840;
  useEffect(() => {
    if (desktop) applyPrefs(getPrefs(), "admin");
  }, [desktop]);
  if (desktop) {
    return (
      <div className="login-desk">
        <div className="card login-card">
          <div className="login-brand"><Brand /><span className="mut">{t("login.title")}</span></div>
          {error && <p className="ahint" style={{ color: "var(--bad)", margin: 0 }}>{error}</p>}
          {step === "email" && (
            <form onSubmit={(e) => { e.preventDefault(); void start(); }} className="login-form">
              <label className="login-field"><span>{t("login.email")}</span><input className="ain" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" autoFocus /></label>
              <button type="submit" className="abtn primary wide" disabled={busy || !email.includes("@")}>{t("login.continue")}</button>
            </form>
          )}
          {step === "password" && (
            <form onSubmit={(e) => { e.preventDefault(); void submitPassword(); }} className="login-form">
              <p className="ahint" style={{ margin: 0 }}>{email}</p>
              <label className="login-field"><span>{t("login.password")}</span><input className="ain" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" autoFocus /></label>
              <button type="submit" className="abtn primary wide" disabled={busy || password.length === 0}>{t("login.continue")}</button>
            </form>
          )}
          {step === "tenant" && choice && (
            <div className="login-form">
              {email && <p className="ahint" style={{ margin: 0 }}>{email}</p>}
              <p className="lbl" style={{ margin: 0 }}>{t("login.chooseTenant")}</p>
              {choice.tenants.map((c) => (
                <button key={c.id} type="button" className="abtn wide tenant-pick" disabled={busy} onClick={() => void pick(c.id)}>
                  <span>{c.name}</span><small className="mut">{roleNames(c.roles)}</small>
                </button>
              ))}
              <p className="ahint" style={{ margin: 0 }}>{t("login.chooseTenantSub")}</p>
            </div>
          )}
          {step === "mfa" && (
            <form onSubmit={(e) => { e.preventDefault(); void submitCode(); }} className="login-form">
              <label className="login-field"><span>{t("login.code")}</span><input className="ain" inputMode="numeric" pattern="[0-9]*" value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" autoFocus /></label>
              <p className="ahint" style={{ margin: 0 }}>{t("login.codeHint")}</p>
              <button type="submit" className="abtn primary wide" disabled={busy || code.length < 6}>{t("login.continue")}</button>
            </form>
          )}
          <p className="ahint login-foot">{t("login.footDesk")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="screen">
      <Strip left={<Brand />} online={navigator.onLine} queued={0} stuck={0} />
      <div className="body" style={{ maxWidth: 480, width: "100%", margin: "0 auto" }}>
        <Prompt title={t("login.title")} />
        {error && <Banner kind="bad" label={t("banner.rejected")} message={error} />}
        {step === "email" && (
          <form onSubmit={(e) => { e.preventDefault(); void start(); }} className="field">
            <label className="field"><span>{t("login.email")}</span><input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" autoFocus /></label>
            <Btn kind="primary" disabled={busy || !email.includes("@")} onClick={() => void start()}>{t("login.continue")}</Btn>
          </form>
        )}
        {step === "password" && (
          <form onSubmit={(e) => { e.preventDefault(); void submitPassword(); }} className="field">
            <p className="hint">{email}</p>
            <label className="field"><span>{t("login.password")}</span><input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" autoFocus /></label>
            <Btn kind="primary" disabled={busy || password.length === 0} onClick={() => void submitPassword()}>{t("login.continue")}</Btn>
          </form>
        )}
        {step === "tenant" && choice && (
          <div className="field">
            <p className="hint">{t("login.chooseTenant")}</p>
            {choice.tenants.map((c) => (
              <Btn key={c.id} kind="secondary" disabled={busy} onClick={() => void pick(c.id)}>{c.name} <small style={{ opacity: 0.7 }}>· {roleNames(c.roles)}</small></Btn>
            ))}
            <p className="hint">{t("login.chooseTenantSub")}</p>
          </div>
        )}
        {step === "mfa" && (
          <form onSubmit={(e) => { e.preventDefault(); void submitCode(); }} className="field">
            <label className="field"><span>{t("login.code")}</span><input className="input" inputMode="numeric" pattern="[0-9]*" value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" autoFocus /></label>
            <p className="hint">{t("login.codeHint")}</p>
            <Btn kind="primary" disabled={busy || code.length < 6} onClick={() => void submitCode()}>{t("login.continue")}</Btn>
          </form>
        )}
      </div>
      <Actions>
        <Btn kind="quiet" onClick={onDevSettings}>{t("login.devSettings")}</Btn>
      </Actions>
    </div>
  );
}

/** Shift login on an enrolled device (R7.24): short code on a big keypad, or a badge scan. */
export function ShiftLogin({ device, onDone, onSettings }: { device: DeviceInfo; onDone: () => void; onSettings: () => void }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const submit = async (secret: { shortCode?: string; badge?: string }) => {
    setBusy(true);
    setError(null);
    try {
      const r = await Auth.shift(secret);
      await adoptSession(r.token, r.expiresAt);
      feedback("accepted");
      onDone();
    } catch {
      feedback("rejected");
      setError(t("shift.failed"));
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    focusScan(el);
    return attachWedge(el, { onScan: (d) => void submit({ badge: d }) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "⌫", "0", "OK"];
  return (
    <div className="screen" onClick={() => { unlockAudio(); focusScan(inputRef.current); }}>
      <Strip left={<Brand />} middle={device.name} online={navigator.onLine} queued={0} stuck={0} />
      <div className="body">
        <Prompt title={t("shift.title")} sub={t("shift.sub", { device: device.name })}>
          {/* A real space collapses to nothing, so the first digit grew the box by a whole line and the keypad jumped (owner 2026-11-03). */}
          <div className="glance"><span className="n">{code.replace(/./g, "•") || "\u00a0"}</span></div>
        </Prompt>
        <ScanBar inputRef={inputRef} placeholder={t("scan.ready")} />
        {error && <Banner kind="bad" label={t("banner.rejected")} message={error} />}
        <div className="keypad">
          {keys.map((k) => (
            <button
              key={k}
              type="button"
              className={`btn ${k === "OK" ? "primary" : "secondary"}`}
              disabled={busy}
              onClick={(e) => {
                e.stopPropagation();
                if (k === "⌫") setCode((c) => c.slice(0, -1));
                else if (k === "OK") void submit({ shortCode: code });
                else setCode((c) => (c.length < 8 ? c + k : c));
              }}
            >
              {k}
            </button>
          ))}
        </div>
      </div>
      <Actions>
        <Btn kind="quiet" icon="back" onClick={onSettings}>{t("menu.settings")}</Btn>
      </Actions>
    </div>
  );
}

export function currentDevice(): DeviceInfo | null {
  return getDevice();
}
